import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, ShieldOff, FileText, AlertTriangle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FneBillingClientCard
 * Affiche dans `Informations Client` :
 *  - Statut Facturation FNE (OUI/NON) + bouton toggle (admin/finance)
 *  - Code TVA effectif calculé live (Module 1)
 *  - Avertissements (NCC manquant, etc.)
 */
const FneBillingClientCard = ({ client, currentUser, onToggled }) => {
  const [tvaInfo, setTvaInfo] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [fullClient, setFullClient] = useState(client);
  const isAdmin = ['admin', 'finance'].includes(currentUser?.role);
  const enabled = !!fullClient?.fneBillingEnabled;

  useEffect(() => {
    if (!client?.id) return;
    // Fetch fneBillingEnabled directly depuis l'historique pour avoir la source de vérité
    axios.get(`${API}/fne-billing/users/${client.id}/fne-history`)
      .then((r) => setFullClient((prev) => ({ ...prev, fneBillingEnabled: r.data.fneBillingEnabled, fneBillingStartDate: r.data.fneBillingStartDate })))
      .catch(() => {});
    axios.get(`${API}/fne-billing/users/${client.id}/effective-tva`)
      .then((r) => setTvaInfo(r.data))
      .catch(() => setTvaInfo(null));
  }, [client?.id, client?.tvaStatus, client?.compteContribuable, client?.companyName]);

  const refreshHistory = () => {
    axios.get(`${API}/fne-billing/users/${client.id}/fne-history`)
      .then((r) => setFullClient((prev) => ({ ...prev, fneBillingEnabled: r.data.fneBillingEnabled, fneBillingStartDate: r.data.fneBillingStartDate })))
      .catch(() => {});
  };

  const toggle = async () => {
    const reason = window.prompt(
      enabled
        ? 'Désactiver la facturation FNE — Raison (obligatoire pour audit OHADA) :'
        : 'Activer la facturation FNE — Raison ou contexte :'
    );
    if (reason === null) return;
    setToggling(true);
    try {
      await axios.patch(`${API}/fne-billing/users/${client.id}/fne-billing`, {
        enabled: !enabled,
        reason: reason || '',
      });
      refreshHistory();
      if (onToggled) onToggled();
    } catch (e) {
      window.alert(`Erreur : ${e?.response?.data?.detail || e.message}`);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="rounded-md border bg-slate-50 p-3 space-y-2" data-testid="fne-billing-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {enabled ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Facturation FNE : OUI
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 inline-flex items-center gap-1">
              <ShieldOff className="w-3.5 h-3.5" /> Facturation FNE : NON
            </Badge>
          )}
          {fullClient?.fneBillingStartDate && (
            <span className="text-[10px] text-slate-500">à partir du {fullClient.fneBillingStartDate}</span>
          )}
        </div>
        {isAdmin && (
          <Button
            type="button"
            size="sm"
            variant={enabled ? 'outline' : 'default'}
            onClick={toggle}
            disabled={toggling}
            data-testid="fne-billing-toggle"
          >
            {toggling && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            {enabled ? 'Désactiver' : 'Activer'}
          </Button>
        )}
      </div>

      {tvaInfo && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-help" data-testid="fne-tva-preview">
                <FileText className="w-3.5 h-3.5" />
                <span>
                  Code TVA effectif (FNE) :{' '}
                  <strong className="font-mono">{tvaInfo.effectiveCode}</strong>{' '}
                  — {tvaInfo.effectiveLabel}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-[11px]">Statut TVA client : {tvaInfo.tvaStatus || 'non défini'}</p>
              <p className="text-[11px]">Template FNE : {tvaInfo.template}</p>
              <p className="text-[10px] text-slate-400 mt-1">
                Le code TVA effectif est calculé dynamiquement à partir du statut TVA du client.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {tvaInfo?.warnings?.includes('client_company_missing_ncc') && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900 inline-flex items-start gap-1.5" data-testid="fne-missing-ncc-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <span>
            NCC client manquant — la certification FNE B2B échouera côté DGI. Compléter le profil.
          </span>
        </div>
      )}
    </div>
  );
};

export default FneBillingClientCard;
